import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, MapPin, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const registeredAgents = [
  {
    platform: 'Meta (Facebook & Instagram)',
    company: 'Meta Platforms, Inc.',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'Handles all Meta properties including Facebook, Instagram, WhatsApp, and Threads.',
    color: '#1877F2'
  },
  {
    platform: 'TikTok',
    company: 'TikTok Inc. / ByteDance Ltd.',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'TikTok operates as a subsidiary of ByteDance. US operations handled through TikTok Inc.',
    color: '#000000'
  },
  {
    platform: 'YouTube',
    company: 'Google LLC',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'YouTube is a subsidiary of Google LLC (Alphabet Inc.).',
    color: '#FF0000'
  },
  {
    platform: 'X (Twitter)',
    company: 'X Corp.',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'Formerly Twitter, Inc. Now operating as X Corp. under new ownership.',
    color: '#000000'
  },
  {
    platform: 'Reddit',
    company: 'Reddit, Inc.',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'Reddit is incorporated in Delaware with principal offices in San Francisco.',
    color: '#FF4500'
  },
  {
    platform: 'Snapchat',
    company: 'Snap Inc.',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'Snap Inc. is the parent company of Snapchat.',
    color: '#FFFC00'
  },
  {
    platform: 'LinkedIn',
    company: 'LinkedIn Corporation',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'LinkedIn is a subsidiary of Microsoft Corporation.',
    color: '#0A66C2'
  },
  {
    platform: 'Pinterest',
    company: 'Pinterest, Inc.',
    agent: 'CT Corporation System',
    address: '1209 Orange Street, Wilmington, DE 19801',
    state: 'Delaware',
    notes: 'Pinterest is incorporated in Delaware.',
    color: '#E60023'
  }
];

const RegisteredAgentPage = () => {
  const scrollToIntake = () => {
    window.location.href = '/#intake';
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-12">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6 bg-[#d9fb06]/20 text-[#d9fb06]">
              Legal Reference
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              Platform Registered Agent Addresses
            </h1>
            <p className="text-lg md:text-xl text-[#888680] mb-8 leading-relaxed">
              Registered agents are designated to receive legal correspondence on behalf of corporations. This information is provided for reference purposes.
            </p>
          </div>
        </section>

        {/* Important Notice */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-12">
          <div className="bg-[#252825] border border-[#d9fb06]/30 rounded-2xl p-6 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-[#d9fb06] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-[#d9fb06] font-semibold mb-2">Important Information</h2>
              <p className="text-[#888680] text-sm">
                Registered agent information can change. Before sending any legal correspondence, verify current information through the appropriate Secretary of State's office. This page is provided for educational purposes only. Social Dispute Co. handles registered agent routing as part of our dispute services.
              </p>
            </div>
          </div>
        </section>

        {/* Registered Agents Grid */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="grid md:grid-cols-2 gap-6">
            {registeredAgents.map((agent) => (
              <div
                key={agent.platform}
                className="bg-[#252825] border border-[#3f4816] rounded-2xl p-6 hover:border-[#d9fb06]/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div 
                      className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-2"
                      style={{ backgroundColor: agent.color + '20', color: agent.color === '#000000' ? '#d9fb06' : agent.color }}
                    >
                      {agent.platform}
                    </div>
                    <h3 className="text-lg font-bold text-[#d9fb06]">{agent.company}</h3>
                  </div>
                  <Building2 className="w-5 h-5 text-[#888680]" />
                </div>
                
                <div className="space-y-3 mb-4">
                  <div>
                    <p className="text-[#888680] text-xs uppercase tracking-wide mb-1">Registered Agent</p>
                    <p className="text-[#d9fb06] text-sm font-medium">{agent.agent}</p>
                  </div>
                  <div>
                    <p className="text-[#888680] text-xs uppercase tracking-wide mb-1">Address</p>
                    <p className="text-[#888680] text-sm flex items-start gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {agent.address}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#888680] text-xs uppercase tracking-wide mb-1">State of Incorporation</p>
                    <p className="text-[#888680] text-sm">{agent.state}</p>
                  </div>
                </div>
                
                <p className="text-[#888680] text-xs border-t border-[#3f4816] pt-4">
                  {agent.notes}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* How to Use This Information */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-[#d9fb06] mb-6">How We Use Registered Agent Information</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-[#d9fb06] font-semibold mb-2">Formal Legal Correspondence</h3>
                <p className="text-[#888680] text-sm">
                  Notices of Dispute and other formal documents are sent via certified mail to registered agents to ensure proper legal receipt.
                </p>
              </div>
              <div>
                <h3 className="text-[#d9fb06] font-semibold mb-2">Documentation Trail</h3>
                <p className="text-[#888680] text-sm">
                  Sending through registered agents creates a formal record of correspondence that can be referenced in arbitration or litigation.
                </p>
              </div>
              <div>
                <h3 className="text-[#d9fb06] font-semibold mb-2">Legal Team Routing</h3>
                <p className="text-[#888680] text-sm">
                  Correspondence received through registered agents is typically routed to legal and compliance teams rather than customer support.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="bg-[#d9fb06] rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a1c1b] mb-4">
              Let Us Handle the Legal Routing
            </h2>
            <p className="text-[#1a1c1b]/80 mb-8 max-w-2xl mx-auto">
              We prepare and send formal dispute documentation through proper legal channels so you don&apos;t have to navigate registered agent requirements yourself.
            </p>
            <Button 
              onClick={scrollToIntake}
              className="bg-[#1a1c1b] text-[#d9fb06] hover:bg-[#1a1c1b]/90 rounded-full px-8 py-6 font-semibold text-base"
              data-testid="agent-cta"
            >
              Submit Your Case for Review
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RegisteredAgentPage;
